import type { InventoryItemRow } from "~/@types/inventoryItemRow";
import type { UnitRow } from "~fulfill/inventory/stock/helpers/inventoryUnits";

import formatLocaleNumber from "~/helpers/localeNumberFormat";
import { formatTruncatedUnits, MIN_STATED_PACK_SIZE, truncateUnits } from "~fulfill/orders/helpers/orderLineUnits";

export interface RowShippingUnit {
	eachesPerUnit: number;
	label: string;
}

export type ShippingUomRow = Pick<InventoryItemRow, "itemShippingUom">;

// wsapi serves eachesPerUnit as a nullable Int: null is a named unit that states no count, never a count of 0 or 1.
const rowShippingUnit = (row: null | ShippingUomRow | undefined): null | RowShippingUnit => {
	const uom = row?.itemShippingUom;
	const eachesPerUnit = uom?.eachesPerUnit;
	const label = uom?.label || uom?.code;
	if (!label || eachesPerUnit == null) return null;
	if (!Number.isInteger(eachesPerUnit) || eachesPerUnit < MIN_STATED_PACK_SIZE) return null;
	return { eachesPerUnit, label };
};

export const sharedShippingUnit = (rows: readonly ShippingUomRow[]): null | RowShippingUnit => {
	const unit = rows.length > 0 ? rowShippingUnit(rows[0]) : null;
	if (!unit) return null;
	const first = rows[0].itemShippingUom;
	const shared = rows.every(
		(row) => row.itemShippingUom?.code === first?.code && row.itemShippingUom?.eachesPerUnit === first?.eachesPerUnit
	);
	return shared ? unit : null;
};

export const unitPairParams = (eaches: number, unit: RowShippingUnit) => ({
	eaches: formatLocaleNumber(eaches),
	unitLabel: unit.label,
	units: formatLocaleNumber(truncateUnits(eaches, unit.eachesPerUnit))
});

export const buildUnitQuantityCells = (eaches: number, unit: RowShippingUnit) => ({
	eaches: formatLocaleNumber(eaches),
	units: formatTruncatedUnits(eaches, unit.eachesPerUnit)
});

export const toUnitRow = (unit: RowShippingUnit | undefined): null | UnitRow =>
	unit ? { itemShippingUom: { code: unit.label, eachesPerUnit: unit.eachesPerUnit, label: unit.label } } : null;

export default rowShippingUnit;
